// Organization sync: a save on one Elements page shows on the others at once
// (Brennan 2026-10-17: Setup and Users "fully in sync where anything changes
// in one reflects instantly in the other").
//
// Every successful write to people, clients, client groups or access
// is announced (services/api.ts calls announceIfOrgWrite after the response).
// The pages that show that data listen with useOrgRefresh and reload:
//   - in this tab, through a window event (Users -> Setup in one session);
//   - in other open tabs, through a BroadcastChannel;
//   - when the tab comes back into view (a change made somewhere else).
// No polling and no stored state: a reload reads the server again.

import { useEffect, useRef } from "react";

const EVENT = "elements:org-changed";
const CHANNEL = "elements-org";

// Writes that change what Setup or Users show. Read-only POSTs (a Setup plan,
// a carry-over dry run) change nothing and are left out.
const ORG_WRITE = [
  /^\/manage\/users(\/|$|\?)/,
  /^\/manage\/clients(\/|$|\?)/,
  /^\/manage\/client-groups(\/|$|\?)/,
  /^\/manage\/access\//,
];
const NOT_A_WRITE = [
  /^\/manage\/access\/setup\/plan/,
  /^\/manage\/access\/carryover\/dry-run/,
];

export function isOrgWrite(method: string, path: string): boolean {
  if (String(method).toUpperCase() === "GET") return false;
  const p = String(path || "");
  if (NOT_A_WRITE.some((re) => re.test(p))) return false;
  return ORG_WRITE.some((re) => re.test(p));
}

let channel: BroadcastChannel | null | undefined;
function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  try {
    channel =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(CHANNEL)
        : null;
  } catch {
    channel = null; // blocked (some privacy modes): this tab still refreshes
  }
  return channel;
}

export function announceOrgChange(path = ""): void {
  const detail = { path, at: Date.now() };
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail }));
  } catch {
    /* no window (tests) */
  }
  try {
    getChannel()?.postMessage(detail);
  } catch {
    /* a closed channel only loses the other tabs' refresh */
  }
}

export function announceIfOrgWrite(method: string, path: string): void {
  if (isOrgWrite(method, path)) announceOrgChange(path);
}

/**
 * Reload when Organization data changes anywhere, or when this tab comes back
 * into view. `reload` should be quiet (no full-page spinner): it runs while the
 * person may be mid-edit. Calls closer together than `minGapMs` collapse into
 * one (a save fires the event and the page's own reload together).
 */
export function useOrgRefresh(reload: () => void, minGapMs = 1500): void {
  const fn = useRef(reload);
  fn.current = reload;
  useEffect(() => {
    let last = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      const now = Date.now();
      const wait = last + minGapMs - now;
      if (wait > 0) {
        if (!timer)
          timer = setTimeout(() => {
            timer = null;
            last = Date.now();
            fn.current();
          }, wait);
        return;
      }
      last = now;
      fn.current();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    window.addEventListener(EVENT, run);
    document.addEventListener("visibilitychange", onVisible);
    const ch = getChannel();
    const onMessage = () => run();
    ch?.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener(EVENT, run);
      document.removeEventListener("visibilitychange", onVisible);
      ch?.removeEventListener("message", onMessage);
      if (timer) clearTimeout(timer);
    };
  }, [minGapMs]);
}
